// Typ: FindAreaFromHeightEquilateralTask
// Temat: Trójkąt równoboczny → Pole z wysokości
// Schemat: dane h → policz a → potem P (odpowiedź to samo P)

import { InlineMath, BlockMath } from "react-katex";
import RootFractionInput, {
  parseRootFractionInput,
  formatLatexPreview
} from "../../../../components/inputs/RootFractionInput";

// ===== helpers =====
function gcd(a, b) {
  a = Math.abs(a); b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

// ===== generator =====
export function generateTask() {
  const a = Math.floor(Math.random() * 8) + 3; // bok 3..10 (całkowity)
  // wysokość w zapisie DOKŁADNYM (bez zaokrągleń)
  const hExactLatex = `\\dfrac{${a}\\sqrt{3}}{2}`;
  return {
    type: "FindAreaFromHeightEquilateralTask",
    data: { a, hExactLatex }
  };
}

// ===== render task =====
export function renderTask({ data }) {
  const { hExactLatex } = data;
  return (
    <div style={{ marginTop: 16, marginBottom: 8 }}>
      Dany jest trójkąt równoboczny o wysokości{" "}
      <InlineMath math={`h=${hExactLatex}`} />. Oblicz jego pole{" "}
      <InlineMath math={'P'} />.
    </div>
  );
}

// ===== input =====
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <div style={{ display: "grid", gap: 10, marginTop: 8 }}>
      <RootFractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        label={'P'}
        // sugerujemy zapis dokładny
        placeholder="np. (49√3)/4, (16√3)/1, 9√3/2"
      />
    </div>
  );
}

// ===== check =====
export function checkAnswer({ data }, userAnswer) {
  const { a } = data;
  const expected = (a * a * Math.sqrt(3)) / 4;

  // 1) standardowy parse z RootFractionInput (obsługuje √ i ułamki)
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  let parsed =
    typeof userAnswer === "object" && userAnswer.parsed != null
      ? userAnswer.parsed
      : parseRootFractionInput(raw);

  // 2) fallback: liczba dziesiętna (np. gdy ktoś mimo wszystko poda przybliżenie)
  if ((parsed == null || !Number.isFinite(parsed)) && typeof raw === "string") {
    const asNumber = Number(raw.replace(",", "."));
    if (Number.isFinite(asNumber)) parsed = asNumber;
  }

  if (parsed == null || !Number.isFinite(parsed)) return false;

  // akceptujemy również zapis dziesiętny do 0.01 (UI zachęca do formy dokładnej)
  return Math.abs(parsed - expected) <= 0.01;
}

// ===== answer =====
export function renderAnswer({ data }) {
  const { a } = data;
  const a2 = a * a;

  // P = (a^2 √3) / 4  -> skróć a^2/4
  const g = gcd(a2, 4);
  const n = a2 / g;
  const d = 4 / g;

  const latex =
    d === 1
      ? (n === 1 ? "P = \\sqrt{3}" : `P = ${n} \\sqrt{3}`)
      : `P = \\dfrac{${n} \\sqrt{3}}{${d}}`;

  return (
    <div style={{ marginTop: 6 }}>
      <InlineMath math={latex} />
    </div>
  );
}

// ===== explanation =====
export function renderExplanation({ data }) {
  const { a, hExactLatex } = data;
  const a2 = a * a;

  const g = gcd(a2, 4);
  const n = a2 / g;
  const d = 4 / g;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div><strong>Krok 1.</strong> Wysokość w trójkącie równobocznym:</div>
      <BlockMath math={'h = \\dfrac{a\\sqrt{3}}{2}'} />

      <div><strong>Krok 2.</strong> Podstawiamy dane i wyznaczamy bok <InlineMath math={'a'} />:</div>
      <BlockMath math={`h = ${hExactLatex}`} />
      <BlockMath math={`a = \\dfrac{2h}{\\sqrt{3}}`} />
      <BlockMath math={`a = \\dfrac{2\\cdot \\left(${hExactLatex}\\right)}{\\sqrt{3}} = ${a}`} />

      <div><strong>Krok 3.</strong> Wzór na pole trójkąta równobocznego:</div>
      <BlockMath math={'P = \\dfrac{a^{2}\\sqrt{3}}{4}'} />

      <div><strong>Krok 4.</strong> Podstawiamy obliczony bok i upraszczamy:</div>
      <BlockMath math={`P = \\dfrac{${a}^{2}\\sqrt{3}}{4}`} />
      <BlockMath math={`P = \\dfrac{${a2}\\sqrt{3}}{4}`} />
      {g > 1 && <BlockMath math={`P = \\dfrac{${n}\\sqrt{3}}{${d}}`} />}
    </div>
  );
}

// ===== user answer =====
export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  if (!raw || typeof raw !== "string" || raw.trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }
  return (
    <div style={{ marginTop: 8 }}>
      <InlineMath math={formatLatexPreview(raw, "P")} />
    </div>
  );
}
